import React, { useEffect, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  X,
  Maximize2,
  Minimize2,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { formatWhatsAppText } from "@/utils/message-parser";
import { HelpArticle, HelpArticleBlock } from "@/lib/quick-actions";
import { ArticleSkeleton, SkeletonStyles, contentInStyle } from "./Skeletons";

interface HelpArticleViewProps {
  articleId: string;
  organizationId: string;
  onBack: () => void;
  onClose?: () => void;
  /** Whether the widget container is currently in its enlarged state. */
  expanded?: boolean;
  /** Toggles the enlarged state. Absent (e.g. mobile fullscreen) hides the button. */
  onToggleExpand?: () => void;
  /** Navigate to another info page (the related-articles links). */
  onOpenArticle?: (articleId: string) => void;
  /** Heading over the related-articles list (default "Related articles"). */
  relatedTitle?: string;
  /** "Did this answer your question?" line (default English). */
  feedbackTitle?: string;
  /** Standalone help-centre URL for this article; absent hides the link. */
  helpCenterUrl?: string;
  /** Label for the help-centre link (default "Open in help center"). */
  helpCenterLabel?: string;
  enableAnimations?: boolean;
}

type FeedbackScore = "sad" | "neutral" | "happy";
const FEEDBACK_EMOJI: Record<FeedbackScore, string> = {
  sad: "😞",
  neutral: "😐",
  happy: "😃",
};

/** "Updated this week"-style line under the title. */
const updatedLabel = (iso: string): string => {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (!Number.isFinite(days) || days < 0) return "Recently updated";
  if (days === 0) return "Updated today";
  if (days === 1) return "Updated yesterday";
  if (days < 7) return "Updated this week";
  if (days < 31) return "Updated this month";
  return `Updated ${new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  })}`;
};

/** Markdown-style links: [label](https://…) → a clean inline anchor. */
const MD_LINK = /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g;

const renderInline = (text: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  let last = 0;
  let i = 0;
  let m: RegExpExecArray | null;
  MD_LINK.lastIndex = 0;
  while ((m = MD_LINK.exec(text))) {
    if (m.index > last) {
      nodes.push(
        <React.Fragment key={`t${i}`}>
          {formatWhatsAppText(text.slice(last, m.index))}
        </React.Fragment>
      );
    }
    nodes.push(
      <a
        key={`l${i}`}
        href={m[2]}
        target="_blank"
        rel="noopener noreferrer"
        className="underline underline-offset-2 break-words"
      >
        {m[1]}
      </a>
    );
    last = m.index + m[0].length;
    i++;
  }
  nodes.push(
    <React.Fragment key="tail">
      {formatWhatsAppText(text.slice(last))}
    </React.Fragment>
  );
  return nodes;
};

/** Multi-line inline-formatted copy (*bold*, _italic_, [label](url) links,
 *  bare clickable URLs). */
const RichText: React.FC<{ text: string; className?: string }> = ({
  text,
  className,
}) => (
  <p className={`whitespace-pre-line ${className || ""}`}>
    {renderInline(text)}
  </p>
);

const Block: React.FC<{ block: HelpArticleBlock }> = ({ block }) => {
  switch (block.type) {
    case "heading":
      return block.level === 2 ? (
        <h3 className="text-lg font-bold text-gray-900 mt-6 mb-2">
          {block.text || ""}
        </h3>
      ) : (
        <h2 className="text-xl font-bold text-gray-900 mt-7 mb-2.5">
          {block.text || ""}
        </h2>
      );
    case "text":
      return (
        <RichText
          text={block.text || ""}
          className="text-[15px] leading-relaxed text-gray-700 my-3"
        />
      );
    case "image": {
      if (!block.url) return null;
      const widthPct =
        typeof block.width_pct === "number" &&
        block.width_pct > 0 &&
        block.width_pct <= 100
          ? block.width_pct
          : 100;
      return (
        <figure className="my-4">
          <img
            src={block.url}
            alt={block.caption || ""}
            loading="lazy"
            className="rounded-xl border border-gray-100 max-w-full h-auto"
            style={{ width: `${widthPct}%` }}
          />
          {block.caption && (
            <figcaption className="text-[13px] text-gray-400 mt-1.5">
              {block.caption}
            </figcaption>
          )}
        </figure>
      );
    }
    case "button": {
      if (!block.url) return null;
      const outline = block.style === "outline";
      return (
        <a
          href={block.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center justify-center my-3 px-5 py-2.5 rounded-xl text-[14px] font-semibold transition-colors ${
            outline
              ? "border border-gray-300 text-gray-800 hover:bg-gray-50"
              : "bg-gray-900 text-white hover:bg-black"
          }`}
        >
          {block.text || block.url}
        </a>
      );
    }
    case "callout":
      return (
        <div
          className="my-4 px-5 py-4 rounded-xl"
          style={{ backgroundColor: block.color || "#EEF0FB" }}
        >
          <RichText
            text={block.text || ""}
            className="text-[14px] leading-relaxed text-gray-800"
          />
        </div>
      );
    case "divider":
      return <hr className="my-6 border-gray-100" />;
    default:
      return null;
  }
};

/**
 * In-widget help-centre article ("info page"): title, updated line, hero
 * image and the ordered content blocks. Always renders on white regardless of
 * the widget's brand colours — same rule as the conversation views.
 */
const HelpArticleView: React.FC<HelpArticleViewProps> = ({
  articleId,
  organizationId,
  onBack,
  onClose,
  expanded = false,
  onToggleExpand,
  onOpenArticle,
  relatedTitle,
  feedbackTitle,
  helpCenterUrl,
  helpCenterLabel,
  enableAnimations = true,
}) => {
  const [article, setArticle] = useState<HelpArticle | null>(null);
  const [related, setRelated] = useState<{ id: string; title: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState<FeedbackScore | null>(null);

  const giveFeedback = async (score: FeedbackScore) => {
    if (feedbackGiven) return;
    setFeedbackGiven(score);
    try {
      // Public insert by design (RLS allows INSERT only; reads are staff-only).
      await supabase.from("widget_article_feedback" as any).insert({
        article_id: articleId,
        organization_id: organizationId,
        score,
        source: "widget",
      });
    } catch {
      /* feedback is best-effort */
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    setArticle(null);
    setRelated([]);
    (async () => {
      try {
        // RLS only exposes published rows to the widget's anon client.
        const { data, error } = await supabase
          .from("widget_help_articles" as any)
          .select(
            "id, title, description, hero_image_url, content, updated_at, related"
          )
          .eq("id", articleId)
          .eq("organization_id", organizationId)
          .maybeSingle();
        if (cancelled) return;
        if (error || !data) {
          setFailed(true);
        } else {
          const row = data as any;
          setArticle({
            ...row,
            content: Array.isArray(row.content) ? row.content : [],
          });
          // Resolve related-article titles (order preserved; unpublished ones
          // are invisible to the anon client and drop out naturally).
          const relIds: string[] = Array.isArray(row.related)
            ? row.related.filter((x: unknown) => typeof x === "string")
            : [];
          if (relIds.length) {
            const { data: rel } = await supabase
              .from("widget_help_articles" as any)
              .select("id, title")
              .in("id", relIds)
              .eq("organization_id", organizationId);
            if (!cancelled && rel) {
              const byId = new Map(
                (rel as any[]).map((r) => [r.id, r as { id: string; title: string }])
              );
              setRelated(
                relIds
                  .map((id) => byId.get(id))
                  .filter(Boolean) as { id: string; title: string }[]
              );
            }
          }
        }
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [articleId, organizationId]);

  return (
    <div
      className="h-full flex flex-col bg-white"
      style={{ fontSize: "16px" }}
    >
      <SkeletonStyles />
      {/* Header controls */}
      <div className="flex items-center justify-between px-3 py-3 flex-shrink-0">
        <button
          onClick={onBack}
          aria-label="Back"
          className="h-9 w-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
        >
          <ChevronLeft size={22} />
        </button>
        <div className="flex items-center gap-1">
          {onToggleExpand && (
            <button
              onClick={onToggleExpand}
              aria-label={expanded ? "Make smaller" : "Make larger"}
              className="h-9 w-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            >
              {expanded ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
            </button>
          )}
          {onClose && (
            <button
              onClick={onClose}
              aria-label="Close"
              className="h-9 w-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            >
              <X size={20} />
            </button>
          )}
        </div>
      </div>

      {/* Article body — overscroll-contain: never chain scroll to the page */}
      <div className="flex-1 overflow-y-auto overscroll-contain">
        <div
          className={`mx-auto px-6 pb-10 ${expanded ? "max-w-3xl" : "max-w-full"}`}
        >
          {loading ? (
            <ArticleSkeleton />
          ) : failed || !article ? (
            <div className="py-16 text-center">
              <p className="text-gray-900 font-semibold mb-1">
                This article isn't available
              </p>
              <p className="text-gray-500 text-sm">
                It may have been unpublished or removed.
              </p>
            </div>
          ) : (
            // The loaded article fades up over where the skeleton sat — the
            // skeleton itself only ever appears after a 120ms delay, so fast
            // loads never flash it (see Skeletons.tsx).
            <div style={contentInStyle}>
              <h1 className="text-[26px] leading-tight font-bold text-gray-900 mt-2">
                {article.title}
              </h1>
              <p className="text-[13px] text-gray-400 mt-2 mb-4">
                {updatedLabel(article.updated_at)}
              </p>
              {article.description && (
                <RichText
                  text={article.description}
                  className="text-[15px] leading-relaxed text-gray-700 mb-3"
                />
              )}
              {article.hero_image_url && (
                <img
                  src={article.hero_image_url}
                  alt=""
                  className="rounded-xl border border-gray-100 max-w-full h-auto my-4"
                />
              )}
              {article.content.map((block, i) => (
                <Block key={i} block={block} />
              ))}

              {/* Related articles — in-widget navigation */}
              {related.length > 0 && onOpenArticle && (
                <div className="mt-8 pt-5 border-t border-gray-100">
                  <h2 className="text-xl font-bold text-gray-900 mb-1">
                    {relatedTitle || "Related articles"}
                  </h2>
                  <div>
                    {related.map((r) => (
                      <button
                        key={r.id}
                        onClick={() => onOpenArticle(r.id)}
                        className="w-full py-3.5 flex items-center justify-between gap-3 text-left group border-b border-gray-50 last:border-b-0"
                      >
                        <span
                          className="text-[15px] text-gray-700 group-hover:text-gray-900"
                          style={{ transition: "color 200ms ease-out" }}
                        >
                          {r.title}
                        </span>
                        <ChevronRight
                          className="text-gray-400 w-4 h-4 flex-shrink-0 group-hover:text-gray-600"
                          style={{ transition: "color 200ms ease-out" }}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Feedback + help-centre footer (the Intercom article footer) */}
              <div className="mt-8 pt-6 border-t border-gray-100 text-center">
                <p className="text-[14px] text-gray-500 mb-3">
                  {feedbackTitle || "Did this answer your question?"}
                </p>
                <div className="flex items-center justify-center gap-6">
                  {(Object.keys(FEEDBACK_EMOJI) as FeedbackScore[]).map(
                    (score) => (
                      <button
                        key={score}
                        onClick={() => giveFeedback(score)}
                        disabled={!!feedbackGiven}
                        aria-label={score}
                        className={`text-[26px] leading-none transition-transform duration-150 ${
                          feedbackGiven === score
                            ? "scale-125"
                            : feedbackGiven
                              ? "opacity-30"
                              : "hover:scale-125"
                        }`}
                      >
                        {FEEDBACK_EMOJI[score]}
                      </button>
                    ),
                  )}
                </div>
                {feedbackGiven && (
                  <p className="text-[12px] text-gray-400 mt-2">✓</p>
                )}
                {helpCenterUrl && (
                  <a
                    href={helpCenterUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 mt-5 text-[13px] text-gray-500 hover:text-gray-800"
                  >
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                    {helpCenterLabel || "Open in help center"}
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HelpArticleView;
